import React, { useEffect, useRef, useState } from 'react'
import { api } from 'core/api/ConnectionManager'
import moment from 'moment'
import helpersSchedule from 'core/helpers/_schedule'
import helpers from 'core/helpers'
import { routes } from 'features/routes'
import omit from 'omit-empty'
import { changeLocation } from 'features/routes'
import { Device } from '../../pages/schedule/_add/schedule_add.local.methods'
import { ISchedule, TScheduelRepeatMode } from '../../core/models/Schedule'
import { IScheduleDeviceDelta, toDeltaPayload } from '../../pages/schedule/_add/scheduleDeviceDelta'

type ValidationCallback = (isValid: boolean, type?: string) => void
type ScheduleTab = 'devices' | 'broadcasts' | 'restrictions'
type ConflictSolution = 'replace' | 'placeAfter'
type BivariantCallback<T> = { bivarianceHack(value: T): void }['bivarianceHack']

export interface ISelectedBroadcast {
    id?: number
    title: string
    [key: string]: unknown
}

interface IRepeatDate {
    startDate: string
    endDate: string
}

type RepeatDay = number | moment.Moment | IRepeatDate

export interface IEditableSchedule {
    id?: number
    startDate: moment.MomentInput
    endDate: moment.MomentInput | null
    startTime: string
    endTime: string
    repeatMode: string
    repeatDays: RepeatDay[]
    localRepeatDays: RepeatDay[]
    timeToUse: string
    contentDownloadDateTime?: null | string | moment.Moment
    priority?: number
    localStartTime?: string
    localEndTime?: string
    localStartDate?: moment.MomentInput
    localEndDate?: moment.MomentInput | null
    removeOverlappingSchedules?: boolean
    dayDuration?: number
    hourDuration?: number
    minuteDuration?: number
}

export interface IScheduleConflict {
    obj: {
        id: number
        name: string
        platform: string
        dsVersion: string
    }
    cause: string
    proposedSolution: string
}

interface IScheduleSolutions {
    replace?: ISchedule
    placeAfter?: ISchedule
}

interface IScheduleSaveData {
    digitalSignageId?: number[]
    groupId?: number[]
    addDigitalSignageIds?: number[]
    removeDigitalSignageIds?: number[]
    addGroupIds?: number[]
    removeGroupIds?: number[]
    id?: number
    scheduleId?: number
    broadcastId?: number
    broadcast?: ISelectedBroadcast
    repeatMode: TScheduelRepeatMode
    repeatDays: RepeatDay[]
    localRepeatDays: RepeatDay[]
    timeToUse: string
    startDate: string
    endDate?: string
    startTime: string
    localStartDate: string
    localEndDate?: string
    localContentDownloadDateTime?: string
    contentDownloadDateTime?: string
    removeOverlappingSchedules?: boolean
}

interface ISaveOptions {
    onSave?: () => void
    tryToSolveConflicts?: boolean
    withoutValidate?: boolean
}

interface IDataToSave {
    saveMethod: 'createSchedule' | 'updateSchedule'
    dataToSave: IScheduleSaveData
    onSave: () => void
    cb: ValidationCallback
}

interface ISaveOptionsState {
    cb: ValidationCallback
    options: ISaveOptions
}

export interface IAddScheduleState {
    tabs: ScheduleTab[]
    selectedTab: ScheduleTab
    groups: number[]
    showConflictsModal: boolean
    schedules: ISchedule[]
    solutions: IScheduleSolutions
    conflicts: IScheduleConflict[]
    applyConflictsSolution: boolean
    selectedSolution: ConflictSolution
    dataToSave: IDataToSave | null
    showWarningWindow: boolean
    saveOptions: ISaveOptionsState | null
}

export interface IAddScheduleRef {
    validateSchedule: (cb: ValidationCallback) => void
    validateDevices: (cb: ValidationCallback, tryToSolveConflicts?: boolean) => void
    save: (cb: ValidationCallback, options?: ISaveOptions) => void
}

interface ISelectedDevicesFromFile {
    selected: Device[]
    notSelected: Device[]
}

export interface IAddScheduleProps {
    onChangeDevices?: (devices: Device[], selectionType?: 'auto') => void
    onChangeBroadcast?: BivariantCallback<ISelectedBroadcast | null>
    onGetConflicts?: (conflicts: IScheduleConflict[]) => void
    emitError?: (err: string) => void
    devices: Device[]
    schedule: IEditableSchedule
    broadcast: ISelectedBroadcast | null
    deferredDownload: { date: moment.MomentInput | null; time: string | null }
    onSave?: (value: boolean) => void
    onChangeSchedule?: (schedule: IEditableSchedule) => void
    changeLocationOnSave: () => void
    onChangeDeferredDownload: (value: string, field: 'contentDownloadDate' | 'contentDownloadTime') => void
    advDuration: number | null
    advContentLength: number | null
    hideConflicts?: boolean
    ref?: React.Ref<unknown>
    addScheduleRef?: React.MutableRefObject<IAddScheduleRef | null>
    onSelectFromFile?: (result: ISelectedDevicesFromFile) => void
    onCancelConfirm?: () => void
    useDeviceDelta?: boolean
    deviceDelta?: IScheduleDeviceDelta
    groupUiDeviceIds?: number[]
    devicesCount?: number
    onToggleCatalogItem?: (data: { selected: boolean; item: Device & { selected?: boolean } }) => void
    onRemoveDevicesByIds?: (ids: number[]) => void
}

const AddScheduleMethods = (p_: IAddScheduleProps) => {
    const [state, setState] = useState<IAddScheduleState>({
        tabs: getTabs(),
        selectedTab: 'devices',
        groups: [],
        showConflictsModal: false,
        schedules: [],
        solutions: {},
        conflicts: [],
        applyConflictsSolution: false,
        selectedSolution: 'replace',
        dataToSave: null,
        showWarningWindow: false,
        saveOptions: null,
    })
    const isSavingRef = useRef(false)

    function getTabs(): ScheduleTab[] {
        const tabs: ScheduleTab[] = []

        if (p_.onChangeDevices) {
            tabs.push('devices')
        }
        if (p_.onChangeBroadcast) {
            tabs.push('broadcasts')
        }

        return tabs
    }

    const releaseSaving = () => {
        isSavingRef.current = false
    }

    const closeWarningWindow = () => {
        releaseSaving()
        setState((prevState) => {
            return {
                ...prevState,
                dataToSave: null,
                showWarningWindow: false,
            }
        })
    }

    const saveDataAndClose = () => {
        if (!state.dataToSave) {
            return
        }

        const { saveMethod, dataToSave, onSave, cb } = state.dataToSave

        api.send(saveMethod, dataToSave)
            .then((res) => {
                if (res) {
                    onSave()
                    cb(true)
                    closeWarningWindow()
                }
            })
            .finally(releaseSaving)
    }

    const validateDevices = (cb: ValidationCallback, tryToSolveConflicts = false) => {
        const s_ = state

        if (p_.useDeviceDelta) {
            if (tryToSolveConflicts && s_.conflicts.length && p_.onRemoveDevicesByIds) {
                p_.onRemoveDevicesByIds(s_.conflicts.map((conflict) => conflict.obj.id))
            }

            const hasDevices = (p_.devicesCount ?? 0) > 0

            if (!hasDevices) {
                const tabs = s_.tabs.filter((tab) => tab !== 'restrictions')

                if (p_.onGetConflicts) {
                    p_.onGetConflicts([])
                }
                setState((prevState) => {
                    return {
                        ...prevState,
                        conflicts: [],
                        tabs,
                        selectedTab: 'devices',
                    }
                })

                if (p_.emitError) {
                    p_.emitError(tryToSolveConflicts ? 'onSolveConflictsAllDevicesRemoved' : 'devicesEmpty')
                }
                cb(false)
                return
            }

            cb(true)
            return
        }

        const devices = removeDeviceFromConflicts(p_.devices)

        if (devices.length === 0) {
            const tabs = s_.tabs.filter((tab) => tab !== 'restrictions')

            if (p_.onGetConflicts) {
                p_.onGetConflicts([])
            }
            setState((prevState) => {
                return {
                    ...prevState,
                    conflicts: [],
                    tabs,
                    selectedTab: 'devices',
                }
            })

            if (tryToSolveConflicts) {
                if (p_.emitError) {
                    p_.emitError('onSolveConflictsAllDevicesRemoved')
                }
            } else {
                if (p_.emitError) {
                    p_.emitError('devicesEmpty')
                }
            }
            cb(false)
            return
        }
        const groupId: number[] = []
        const digitalSignageId: number[] = []
        const groupModel: Device[] = []

        p_.devices.forEach((device) => {
            if (device.type === 'group') {
                groupId.push(device.id)
                groupModel.push(device)
            } else {
                digitalSignageId.push(device.id)
            }
        })

        setState((prevState) => {
            return {
                ...prevState,
                groups: groupId,
            }
        })

        api.send<{ groupId: number[]; digitalSignageId: number[] }, Device[]>('getDisplaysFromGroups', {
            groupId,
            digitalSignageId,
        }).then((devices) => {
            const devicesList = devices.map((device) => ({ ...device, type: 'digitalSignage' }))

            removeDeviceFromConflicts(devicesList, groupModel)

            cb(true)
        })
    }
    const removeDeviceFromConflicts = (devices: Device[], groups: Device[] = []) => {
        const s_ = state

        if (s_.conflicts.length && s_.applyConflictsSolution) {
            const devicesIdToDelete = s_.conflicts.map((conflict) => conflict.obj.id)
            const filteredDevices = devices.filter((device) => !devicesIdToDelete.includes(device.id))

            if (p_.onChangeDevices) {
                p_.onChangeDevices([...filteredDevices, ...groups], 'auto')
            }

            return filteredDevices
        }

        if (p_.onChangeDevices) {
            p_.onChangeDevices([...devices, ...groups], 'auto')
        }

        return devices
    }
    const validateSchedule = (cb: ValidationCallback) => {
        let isValid = true

        const startTime = moment(p_.schedule.startTime, 'HH:mm:ss').unix()
        const endTime = moment(p_.schedule.endTime, 'HH:mm:ss').unix()

        if (endTime < startTime) {
            isValid = false
            if (p_.emitError) {
                p_.emitError('endTimeLessStartTime')
            }
        }

        if (!p_.schedule.repeatDays.length) {
            if (p_.schedule.repeatMode === 'weekly') {
                isValid = false
                if (p_.emitError) {
                    p_.emitError('repeatDaysEmpty')
                }
            }
            if (p_.schedule.repeatMode === 'exact_dates') {
                isValid = false
                if (p_.emitError) {
                    p_.emitError('selectAtLeastOneDate')
                }
            }
        }

        cb(isValid)
    }
    const validateBroadcast = (cb: ValidationCallback) => {
        const isValid = Boolean(p_.broadcast)
        if (!isValid) {
            if (p_.emitError) {
                p_.emitError(p_.onChangeBroadcast ? 'selectBroadcastsEmpty' : 'createBroadcastError')
            }
        }

        cb(isValid)
    }
    const validateDeferredDownload = (cb: ValidationCallback) => {
        let isValid = true

        if (p_.deferredDownload) {
            if (!p_.deferredDownload.date && p_.deferredDownload.time) {
                if (p_.emitError) {
                    p_.emitError('fillDeferredDate')
                }
                isValid = false
            }

            if (p_.deferredDownload.date && !p_.deferredDownload.time) {
                if (p_.emitError) {
                    p_.emitError('fillDeferredTime')
                }
                isValid = false
            }

            if (p_.deferredDownload.date && p_.deferredDownload.time && isValid) {
                const date = moment(p_.deferredDownload.date).format('YYYY-MM-DD')
                const contentDownloadDateTime = moment(`${date} ${p_.deferredDownload.time}`)
                const startDateTime = moment(
                    `${moment(p_.schedule.startDate).format('YYYY-MM-DD')} ${p_.schedule.startTime}`
                )

                if (startDateTime.isBefore(contentDownloadDateTime)) {
                    if (p_.emitError) {
                        p_.emitError('contentDownloadCantBeLongestStartDateTime')
                    }
                    isValid = false
                }
            }
        }

        cb(isValid)
    }

    useEffect(() => {
        if (state.saveOptions) {
            saveMethod(state.saveOptions.cb, state.saveOptions.options)
            setState((prevState) => {
                return {
                    ...prevState,
                    saveOptions: null,
                }
            })
        }
    }, [state.saveOptions])
    const save = (cb: ValidationCallback, options: ISaveOptions = {}) => {
        if (isSavingRef.current) {
            return
        }
        isSavingRef.current = true

        const onSaveResult = (isValid: boolean, type?: string) => {
            if (!isValid) {
                releaseSaving()
            }
            cb(isValid, type)
        }

        validateBroadcast((isBroadcastValid: boolean) => {
            if (isBroadcastValid) {
                validateDevices((isValidDevices: boolean) => {
                    if (isValidDevices) {
                        validateSchedule((isValidSchedule: boolean) => {
                            if (isValidSchedule) {
                                validateDeferredDownload((isValidDeferred: boolean) => {
                                    if (isValidDeferred) {
                                        setState((prevState) => {
                                            return {
                                                ...prevState,
                                                saveOptions: {
                                                    cb: onSaveResult,
                                                    options,
                                                },
                                            }
                                        })
                                    } else {
                                        onSaveResult(false)
                                    }
                                })
                            } else {
                                onSaveResult(false)
                            }
                        })
                    } else {
                        onSaveResult(false, 'devices')
                    }
                }, Boolean(options.tryToSolveConflicts))
            } else {
                onSaveResult(false)
            }
        })
    }
    const saveMethod = (cb: ValidationCallback, options: ISaveOptions = {}) => {
        const s_ = state

        const onSave = options.onSave ? options.onSave : () => {}
        const devices: Device[] = p_.devices.filter((device: Device) => device.type !== 'group')
        const schedule = helpersSchedule.getSchedule(
            (p_.schedule as unknown) as ISchedule,
            p_.useDeviceDelta ? [] : devices
        ) as Omit<IScheduleSaveData, 'digitalSignageId' | 'addDigitalSignageIds'>
        const dataToSave: IScheduleSaveData = {
            ...schedule,
        }

        delete dataToSave.digitalSignageId

        if (p_.useDeviceDelta && p_.deviceDelta) {
            Object.assign(dataToSave, toDeltaPayload(p_.deviceDelta))
            delete dataToSave.digitalSignageId
            delete dataToSave.groupId
        } else {
            const deviceIds = devices.map((device) => device.id)
            if (p_.schedule.id) {
                dataToSave.digitalSignageId = deviceIds
            } else {
                dataToSave.addDigitalSignageIds = deviceIds
            }
            dataToSave.groupId = s_.groups
            if (!s_.groups.length) {
                delete dataToSave.groupId
            }
        }
        if (p_.schedule.id) {
            dataToSave.id = p_.schedule.id
        }
        if (p_.broadcast?.id) {
            dataToSave.broadcastId = p_.broadcast.id
        } else if (p_.broadcast) {
            dataToSave.broadcast = p_.broadcast
        }
        if (dataToSave.repeatMode === 'weekly') {
            if (dataToSave.timeToUse !== 'local') {
                const startDate = moment
                    .utc(`${dataToSave.startDate} ${dataToSave.startTime}`, 'YYYY-MM-DD HH:mm:ss')
                    .format()
                dataToSave.repeatDays = helpersSchedule.formatRepeatDays(dataToSave.repeatDays as number[], startDate)
            } else {
                dataToSave.repeatDays = dataToSave.localRepeatDays
            }
        }
        if (dataToSave.repeatMode === 'exact_dates') {
            const repeatDays = dataToSave.repeatDays as IRepeatDate[]
            const localRepeatDays = dataToSave.localRepeatDays as IRepeatDate[]

            dataToSave.startDate = repeatDays[0].startDate
            dataToSave.localStartDate = localRepeatDays[0].startDate

            if (repeatDays.length === 1) {
                dataToSave.endDate = dataToSave.startDate
                dataToSave.localEndDate = dataToSave.localStartDate
            } else {
                dataToSave.endDate = repeatDays[repeatDays.length - 1].endDate
                dataToSave.localEndDate = localRepeatDays[repeatDays.length - 1].endDate
            }
        }

        if (p_.deferredDownload) {
            const dateTime = omit(p_.deferredDownload)
            const date = p_.deferredDownload.date

            if (Object.keys(dateTime).length) {
                dataToSave.localContentDownloadDateTime = moment(
                    `${moment(date as moment.MomentInput).format('YYYY-MM-DD')} ${p_.deferredDownload.time}`
                ).format('YYYY-MM-DD HH:mm:ss')
                dataToSave.contentDownloadDateTime = moment(
                    `${moment(date as moment.MomentInput).format('YYYY-MM-DD')} ${p_.deferredDownload.time}`
                )
                    .utc()
                    .toISOString()
            }
        }

        const conflictPayload: IScheduleSaveData =
            p_.useDeviceDelta && dataToSave.id ? { ...dataToSave, scheduleId: dataToSave.id } : dataToSave

        if (!options.withoutValidate) {
            api.send<
                IScheduleSaveData,
                {
                    isValid: boolean
                    conflicts: IScheduleConflict[]
                }
            >('getBroadcastRestrictionsForSchedule', conflictPayload)
                .then((res) => {
                    if (!res.isValid) {
                        let tabs = s_.tabs
                        const restrictionsTab = 'restrictions'

                        if (!tabs.includes(restrictionsTab)) {
                            tabs.push(restrictionsTab)
                        }
                        p_.onGetConflicts?.(res.conflicts)

                        setState((prevState) => {
                            return {
                                ...prevState,
                                tabs,
                                selectedTab: restrictionsTab,
                                conflicts: res.conflicts,
                            }
                        })

                        cb(false)
                        return
                    }

                    api.send<
                        IScheduleSaveData,
                        {
                            schedules: ISchedule[]
                            solutions: IScheduleSolutions
                        }
                    >('getOverlappingSchedules', { ...conflictPayload })
                        .then((res) => {
                            if (!res.schedules.length) {
                                const saveMethod = dataToSave.id ? 'updateSchedule' : 'createSchedule'

                                if (saveMethod === 'createSchedule') {
                                    return api.send(saveMethod, dataToSave)
                                }

                                setState((prevState) => {
                                    return {
                                        ...prevState,
                                        dataToSave: {
                                            saveMethod,
                                            dataToSave,
                                            onSave,
                                            cb,
                                        },
                                        showWarningWindow: true,
                                    }
                                })

                                return
                            }

                            setState((prevState) => {
                                return {
                                    ...prevState,
                                    showConflictsModal: true,
                                    schedules: res.schedules,
                                    solutions: res.solutions,
                                }
                            })
                            cb(false)
                        })
                        .then((res) => {
                            if (res) {
                                onSave()
                                cb(true)
                            } else {
                                releaseSaving()
                            }
                        })
                        .catch(releaseSaving)
                })
                .catch(releaseSaving)
        }
        if (options.withoutValidate) {
            const saveMethod = dataToSave.id ? 'updateSchedule' : 'createSchedule'
            api.send(saveMethod, dataToSave)
                .then((res) => {
                    if (res) {
                        onSave()
                        cb(true)
                    } else {
                        releaseSaving()
                    }
                })
                .catch(releaseSaving)
        }
    }
    const closeConflictsModal = () => {
        setState((prevState) => {
            return {
                ...prevState,
                showConflictsModal: false,
                schedules: [],
                solutions: {},
            }
        })
    }
    const solveConflicts = () => {
        setState((prevState) => {
            return {
                ...prevState,
                showConflictsModal: false,
                schedules: [],
                solutions: {},
                applyConflictsSolution: true,
            }
        })
        setTimeout(() => {
            save(
                (isValid: boolean) => {
                    if (isValid) {
                        changeLocation(`/${routes.schedule.path}`)
                    }
                },
                { onSave: () => (p_.onSave ? p_.onSave(true) : () => {}), tryToSolveConflicts: true }
            )
        }, 100)
    }
    const saveWithSolution = () => {
        const s_ = state
        let data: IEditableSchedule = {
            ...p_.schedule,
        }

        if (s_.selectedSolution === 'placeAfter') {
            const scheduleItem = s_.solutions.placeAfter

            if (!scheduleItem) {
                return
            }

            const startDate = helpers.getLocalDate({ date: scheduleItem.startDate, time: scheduleItem.startTime })
            const endDate = helpers.getLocalDate({ date: scheduleItem.endDate, time: scheduleItem.endTime })

            data = {
                ...data,
                startDate: startDate!,
                endDate,
                startTime: startDate!.format('HH:mm:ss'),
            }

            if (endDate) {
                data.endTime = endDate!.format('HH:mm:ss')
            }
        }

        if (s_.selectedSolution === 'replace') {
            data.removeOverlappingSchedules = true
        }

        p_.onChangeSchedule?.(data)
        setState((prevState) => {
            return {
                ...prevState,
                showConflictsModal: false,
            }
        })
        setTimeout(() => {
            save(
                () => {
                    if (p_.changeLocationOnSave) {
                        p_.changeLocationOnSave()
                        return
                    }

                    changeLocation(`/${routes.schedule.path}`)
                },
                { withoutValidate: true, onSave: () => (p_.onSave ? p_.onSave(true) : () => {}) }
            )
        }, 100)
    }

    useEffect(() => {
        if (p_.addScheduleRef) {
            p_.addScheduleRef.current = { validateSchedule, validateDevices, save }
        }
    }, [p_])

    return {
        state,
        setState,
        solveConflicts,
        closeWarningWindow,
        saveDataAndClose,
        closeConflictsModal,
        saveWithSolution,
    }
}

export default AddScheduleMethods
